interface ServicesProps {
  onQuoteClick: () => void;
}

const services = [
  {
    title: 'Post-Construction Cleaning',
    description: 'Complete cleanup after construction wraps — removing dust, debris, and residue so your project is ready for final walkthrough and occupancy.',
    icon: 'ri-building-line',
    image: 'https://public.readdy.ai/ai/img_res/e284e76893820904b9bda7a29106c229.jpg',
    featured: true,
  },
  {
    title: 'Final Cleaning',
    description: 'The last pass before handoff — detailed surface cleaning, window polishing, and fixture detailing that makes every space shine.',
    icon: 'ri-sparkling-line',
    image: 'https://readdy.ai/api/search-image?query=professional%20final%20cleaning%20of%20luxury%20commercial%20interior%2C%20spotless%20polished%20floors%2C%20gleaming%20surfaces%2C%20clean%20modern%20office%20space%2C%20professional%20cleaner%20polishing%20windows%2C%20bright%20clean%20atmosphere%2C%20minimal%20dark%20background%20with%20crisp%20white%20surfaces%2C%20high%20end%20photography&width=600&height=400&seq=svc002&orientation=landscape',
    featured: false,
  },
  {
    title: 'Rough Cleaning',
    description: 'First-phase cleanup during active construction — clearing bulk debris, sweeping, and prepping the site for continued build progress.',
    icon: 'ri-tools-line',
    image: 'https://readdy.ai/api/search-image?query=rough%20construction%20site%20cleaning%2C%20workers%20removing%20debris%20and%20construction%20waste%2C%20industrial%20building%20interior%2C%20heavy%20duty%20cleaning%20equipment%2C%20dusty%20construction%20environment%2C%20dark%20moody%20industrial%20atmosphere%2C%20professional%20crew%20in%20safety%20gear%2C%20dramatic%20lighting&width=600&height=400&seq=svc003&orientation=landscape',
    featured: false,
  },
  {
    title: 'Commercial Cleaning',
    description: 'Ongoing and one-time commercial cleaning for offices, retail spaces, and business facilities — maintaining a professional environment.',
    icon: 'ri-store-2-line',
    image: 'https://readdy.ai/api/search-image?query=commercial%20office%20cleaning%20service%2C%20professional%20cleaner%20in%20modern%20office%20building%2C%20clean%20corporate%20interior%2C%20polished%20floors%20and%20glass%20surfaces%2C%20bright%20professional%20environment%2C%20dark%20charcoal%20and%20white%20color%20scheme%2C%20high%20contrast%20professional%20photography&width=600&height=400&seq=svc004&orientation=landscape',
    featured: false,
  },
  {
    title: 'Debris & Dust Removal',
    description: 'Targeted removal of construction dust, drywall particles, and material waste — protecting surfaces and air quality throughout the project.',
    icon: 'ri-delete-bin-line',
    image: 'https://readdy.ai/api/search-image?query=construction%20dust%20and%20debris%20removal%2C%20industrial%20vacuum%20cleaning%20equipment%2C%20dusty%20construction%20site%20being%20cleaned%2C%20workers%20with%20professional%20equipment%2C%20dramatic%20dust%20particles%20in%20light%20beams%2C%20dark%20industrial%20atmosphere%2C%20high%20contrast%20photography&width=600&height=400&seq=svc005&orientation=landscape',
    featured: false,
  },
];

export default function Services({ onQuoteClick }: ServicesProps) {
  return (
    <section id="services" className="bg-[#f5f4f0] py-24 px-6 md:px-16 lg:px-24">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
        <div>
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-[#C9A84C]"></div>
            <span className="font-inter text-[#C9A84C] text-xs tracking-widest uppercase">What We Do</span>
          </div>
          <h2 className="font-barlow-condensed font-900 text-[#1a1a1a] text-4xl md:text-5xl lg:text-6xl leading-none">
            CLEANING SERVICES<br />
            <span className="text-[#C9A84C]">BUILT FOR BUILDERS</span>
          </h2>
        </div>
        <p className="font-inter text-[#1a1a1a]/60 text-sm leading-relaxed max-w-sm">
          Every service is designed around the demands of active construction sites and commercial projects in Las Vegas.
        </p>
      </div>

      {/* Featured + Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Featured card */}
        <div className="lg:col-span-1 relative rounded-sm overflow-hidden group cursor-pointer min-h-[420px]">
          <img
            src={services[0].image}
            alt={services[0].title}
            className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          {/* Stronger overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20"></div>
          <div className="absolute inset-0 p-8 flex flex-col justify-end">
            <div className="w-10 h-10 flex items-center justify-center bg-[#C9A84C] rounded-sm mb-4">
              <i className={`${services[0].icon} text-white text-lg`}></i>
            </div>
            <h3 className="font-barlow font-800 text-white text-xl mb-2 drop-shadow-lg">{services[0].title}</h3>
            <p className="font-inter text-white/90 text-sm leading-relaxed drop-shadow-md">{services[0].description}</p>
          </div>
        </div>

        {/* Right grid */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {services.slice(1).map((service) => (
            <div key={service.title} className="relative rounded-sm overflow-hidden group cursor-pointer min-h-[200px]">
              <img
                src={service.image}
                alt={service.title}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              {/* Much stronger overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/25"></div>
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <div className="w-8 h-8 flex items-center justify-center bg-[#C9A84C] rounded-sm mb-3 shrink-0">
                  <i className={`${service.icon} text-white text-sm`}></i>
                </div>
                {/* Title with solid dark pill background for guaranteed readability */}
                <h3 className="font-barlow font-800 text-white text-sm mb-1.5 drop-shadow-lg">{service.title}</h3>
                <p className="font-inter text-white/85 text-xs leading-relaxed drop-shadow-md">{service.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-12 text-center">
        <button
          onClick={onQuoteClick}
          className="inline-flex items-center gap-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white font-barlow font-700 text-sm px-10 py-4 rounded-sm transition-all duration-200 cursor-pointer whitespace-nowrap tracking-widest uppercase"
        >
          Request a Cleaning Quote
          <i className="ri-arrow-right-line text-[#C9A84C]"></i>
        </button>
      </div>
    </section>
  );
}
